import type { GenericFormType } from '@neo/blocks/forms/types'
import { z } from 'zod'
import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import { EnhancedTextArea } from 'v2/shared/components/enhanced-textarea'
import { type AiAgentNodeformData, FlowNodeTypes } from '../../types'
import { NodevarList } from '../../components/nodevar-list'

export const AIInstructions = ({
  form,
  nodeVars,
  flowNodeType,
}: {
  form: GenericFormType<AiAgentNodeformData>
  nodeVars: string[]
  flowNodeType: FlowNodeTypes
}) => (
  <div className="flex flex-col">
    <FieldsRenderer
      form={form}
      fields={[
        {
          label: 'Add instructions',
          name: 'instructions',
          type: 'custom',
          component: ({ value, onChange, ...props }) => (
            <EnhancedTextArea
              value={value as string}
              onChange={onChange}
              showFileUpload={flowNodeType !== FlowNodeTypes.ai_agent_call}
              className="min-h-[30px]"
              {...props}
            />
          ),
          eventDataExtractor: (e) => e,
          zodValidation: z.string().min(1, 'Instructions are required'),
          inputProps: {
            placeholder:
              'Provide detailed instructions to guide the AI on how to handle users queries.',
            rows: 4,
            endIcon: (
              <NodevarList nodevars={nodeVars} inputElementSelector="#instructions .ql-ref" />
            ),
            // className: 'mt-2.5',
            'data-testid': 'textarea-ai-instructions',
          },
        },
        {
          label: 'Disable inequipped to serve',
          name: 'disable_inequipped_to_serve',
          componentType: 'checkbox',
          className: 'mt-4',
          zodValidation: z.boolean().optional(),
          visible: (values) => values.channel !== 'call',
        },
        {
          label: 'Conversation completion criteria',
          name: 'completion_criteria',
          className: 'mt-4',
          zodValidation: (values) =>
            values.conversation_type === 'ai_generated'
              ? z.string().min(1, 'Completion criteria is required.')
              : z.string().nullable(),
          inputProps: {
            placeholder:
              "Define the condition to proceed or loop at this node. Example: Move forward after capturing the user's name",
            rows: 4,
            // className: 'mt-2.5',
            'data-testid': 'textarea-completion-criteria',
            endIcon: (
              <NodevarList
                nodevars={nodeVars}
                inputElementSelector="#completion_criteria .ql-ref"
              />
            ),
          },
          type: 'custom',
          component: ({ value, onChange, ...props }) => (
            <EnhancedTextArea
              value={value as string}
              onChange={onChange}
              showFileUpload={flowNodeType !== FlowNodeTypes.ai_agent_call}
              className="min-h-[60px]"
              {...props}
            />
          ),
          eventDataExtractor: (e) => e,
        },
        {
          name: 'disable_human_handoff',
          label: '',
          componentType: 'switch',
          valueFormatter: (value) => !value,
          eventDataExtractor: (e) => !e,
          className: 'flex justify-end relative top-3 z-50',
          zodValidation: z.boolean(),
          type: 'sub-item',
        },
        {
          label: 'Human handoff instructions',
          name: 'human_handoff_instructions',
          component: ({ value, onChange, ...props }) => (
            <EnhancedTextArea
              value={value as string}
              onChange={onChange}
              {...props}
              showFileUpload={false}
              className="min-h-[30px]"
            />
          ),
          eventDataExtractor: (e) => e,
          zodValidation: (values) =>
            values.disable_human_handoff
              ? z.string().optional()
              : z
                  .string()
                  .min(1, 'Handoff instructions are required when human handoff is enabled'),
          getDynamicProps: (values) => ({
            inputProps: {
              placeholder: 'Provide instructions for the human handoff',
              rows: 4,
              'data-testid': 'textarea-handoff-instructions',
              endIcon: (
                <NodevarList
                  className={values.disable_human_handoff ? 'pointer-events-none' : 'ml-auto'}
                  nodevars={nodeVars}
                  inputElementSelector="#human_handoff_instructions .ql-ref"
                />
              ),
              className: values.disable_human_handoff
                ? 'bg-bg3 text-muted-foreground disabled:opacity-100'
                : '',
            },
          }),
          onChangeListenTo: ['disable_human_handoff'],
          disabled: (values) => values.disable_human_handoff,
          type: 'custom',
        },
        {
          label: 'No response instructions',
          name: 'no_response_instructions',
          type: 'custom',
          component: ({ value, onChange, ...props }) => (
            <EnhancedTextArea value={value as string} onChange={onChange} {...props} />
          ),
          eventDataExtractor: (e) => e,
          zodValidation: z.string().min(1, 'No response instructions are required'),
          inputProps: {
            placeholder: 'ask design to provide placeholder',
            rows: 4,
            // className: 'mt-2.5',
            'data-testid': 'textarea-handoff-instructions',
          },
          visible: false,
        },
        {
          label: 'No Response Timeout',
          name: 'no_response_timeout',
          componentType: 'input',
          zodValidation: z.number().min(1, 'No response timeout is required'),
          inputProps: {
            type: 'number',
            defaultValue: 3600,
            min: 1, // check with BE
            max: 3600, // check with BE
            'data-testid': 'input-no-response-timeout',
          },
          visible: false,
          type: 'sub-item',
        },
      ]}
    />
  </div>
)
